import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Check, X, MessageSquare, UserMinus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { MentorshipConnection } from '@/types/mentorship';

interface ConnectionCardProps {
  connection: MentorshipConnection;
  onAccept?: (id: string) => void;
  onDecline?: (id: string) => void;
  onMessage?: (id: string) => void;
  onEnd?: (id: string) => void;
}

interface ProfileInfo {
  display_name: string | null;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  job_role: string | null;
}

export function ConnectionCard({ connection, onAccept, onDecline, onMessage, onEnd }: ConnectionCardProps) {
  const { user } = useAuth();
  const [otherProfile, setOtherProfile] = useState<ProfileInfo | null>(null);
  
  const isMentor = connection.mentor_id === user?.id;
  const otherUserId = isMentor ? connection.mentee_id : connection.mentor_id;

  useEffect(() => {
    const fetchProfile = async () => {
      // Use profiles_public view to get only non-sensitive profile data for connections
      const { data } = await supabase
        .from('profiles_public')
        .select('display_name, avatar_url, first_name, last_name, job_role')
        .eq('user_id', otherUserId)
        .single();
      
      if (data) {
        setOtherProfile(data);
      }
    };

    fetchProfile();
  }, [otherUserId]);

  const getInitials = () => {
    if (otherProfile?.first_name && otherProfile?.last_name) {
      return `${otherProfile.first_name[0]}${otherProfile.last_name[0]}`.toUpperCase();
    }
    if (otherProfile?.display_name) {
      return otherProfile.display_name.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getName = () => {
    if (otherProfile?.first_name && otherProfile?.last_name) {
      return `${otherProfile.first_name} ${otherProfile.last_name}`;
    }
    return otherProfile?.display_name || 'User';
  };

  const getStatusColor = () => {
    switch (connection.status) {
      case 'pending':
        return 'bg-warning/15 text-warning border-warning/30';
      case 'accepted':
        return 'bg-success/15 text-success border-success/30';
      case 'declined':
        return 'bg-destructive/15 text-destructive border-destructive/30';
      case 'ended':
        return 'bg-muted text-muted-foreground';
      default:
        return '';
    }
  };

  return (
    <Card className="card-elevated">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-4">
          <Avatar className="w-12 h-12">
            <AvatarImage src={otherProfile?.avatar_url || undefined} alt={getName()} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base truncate">{getName()}</CardTitle>
              <Badge variant="outline" className={getStatusColor()}>
                {connection.status}
              </Badge>
            </div>
            <CardDescription className="mt-0.5">
              {isMentor ? 'Mentee' : 'Mentor'} • {connection.skill_focus || 'General mentorship'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {connection.message && connection.status === 'pending' && isMentor && (
          <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            "{connection.message}"
          </p>
        )}

        <div className="flex gap-2">
          {connection.status === 'pending' && isMentor && onAccept && onDecline && (
            <>
              <Button 
                variant="default" 
                size="sm" 
                className="flex-1"
                onClick={() => onAccept(connection.id)}
              >
                <Check className="w-4 h-4 mr-1" />
                Accept
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onDecline(connection.id)}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}

          {connection.status === 'pending' && !isMentor && (
            <Button variant="outline" size="sm" className="flex-1" disabled>
              Awaiting response...
            </Button>
          )}

          {connection.status === 'accepted' && (
            <>
              <Button 
                variant="default" 
                size="sm" 
                className="flex-1"
                onClick={() => onMessage?.(connection.id)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onEnd?.(connection.id)}
              >
                <UserMinus className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
